<template>
  <div class="p-3">
    <h1 class="h5 mb-3">Safe area / Insets debug</h1>

    <div class="mb-3">
      <button class="btn btn-sm btn-primary me-2" @click="refresh">Refresh</button>
      <span class="text-muted small">Auto-updates on resize/orientation</span>
    </div>

    <div class="card mb-3">
      <div class="card-header">CSS variables (computed)</div>
      <div class="card-body p-0">
        <table class="table table-sm mb-0">
          <thead>
            <tr>
              <th style="width: 55%">Variable</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.name">
              <td>
                <code>{{ row.name }}</code>
              </td>
              <td>
                <code>{{ row.value || '(empty)' }}</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card">
      <div class="card-header">Viewport info</div>
      <div class="card-body">
        <div class="small">
          <div>
            <strong>window.innerWidth/innerHeight:</strong> {{ viewport.innerWidth }} ×
            {{ viewport.innerHeight }}
          </div>
          <div>
            <strong>documentElement.clientWidth/clientHeight:</strong> {{ viewport.clientWidth }} ×
            {{ viewport.clientHeight }}
          </div>
          <div>
            <strong>visualViewport (if any):</strong> {{ viewport.vvWidth }} ×
            {{ viewport.vvHeight }} (scale {{ viewport.vvScale }})
          </div>
          <div><strong>100vh in px (measured):</strong> {{ viewport.vh100 }}</div>
          <div><strong>100dvh in px (measured):</strong> {{ viewport.dvh100 }}</div>
          <div><strong>100svh in px (measured):</strong> {{ viewport.svh100 }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'

type Row = { name: string; value: string }

const VARS = [
  '--safe-top',
  '--safe-right',
  '--safe-bottom',
  '--safe-left',

  '--cap-safe-top',
  '--cap-safe-right',
  '--cap-safe-bottom',
  '--cap-safe-left',

  '--native-safe-top',
  '--native-safe-right',
  '--native-safe-bottom',
  '--native-safe-left',
]

const values = ref<Record<string, string>>({})

const viewport = reactive({
  innerWidth: 0,
  innerHeight: 0,
  clientWidth: 0,
  clientHeight: 0,
  vvWidth: 0,
  vvHeight: 0,
  vvScale: 1,
  vh100: 0,
  dvh100: 0,
  svh100: 0,
})

function readCssVar(name: string) {
  const cs = getComputedStyle(document.documentElement)
  return cs.getPropertyValue(name).trim()
}

function measureUnit(unit: 'vh' | 'dvh' | 'svh') {
  const el = document.createElement('div')
  el.style.position = 'fixed'
  el.style.left = '-9999px'
  el.style.top = '0'
  el.style.height = `100${unit}`
  el.style.width = '1px'
  document.body.appendChild(el)
  const px = el.getBoundingClientRect().height
  document.body.removeChild(el)
  return Math.round(px)
}

function refresh() {
  const next: Record<string, string> = {}
  for (const v of VARS) next[v] = readCssVar(v)
  values.value = next

  viewport.innerWidth = window.innerWidth
  viewport.innerHeight = window.innerHeight
  viewport.clientWidth = document.documentElement.clientWidth
  viewport.clientHeight = document.documentElement.clientHeight

  const vv = window.visualViewport
  viewport.vvWidth = vv ? Math.round(vv.width) : 0
  viewport.vvHeight = vv ? Math.round(vv.height) : 0
  viewport.vvScale = vv ? vv.scale : 1

  // These help spot "double insets" vs "vh includes system bars"
  viewport.vh100 = measureUnit('vh')
  viewport.dvh100 = measureUnit('dvh')
  viewport.svh100 = measureUnit('svh')
}

const rows = computed<Row[]>(() => VARS.map((name) => ({ name, value: values.value[name] ?? '' })))

let resizeTimer: number | null = null
function onResize() {
  if (resizeTimer) window.clearTimeout(resizeTimer)
  resizeTimer = window.setTimeout(() => refresh(), 50)
}

onMounted(() => {
  refresh()
  window.addEventListener('resize', onResize)
  window.addEventListener('orientationchange', onResize)
  window.visualViewport?.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  window.removeEventListener('orientationchange', onResize)
  window.visualViewport?.removeEventListener('resize', onResize)
  if (resizeTimer) window.clearTimeout(resizeTimer)
})
</script>
